import { type RoutingChoice, type RoutingPolicy, type RoutingRule, routeAgents } from "@minga/protocol"
import { sha256Hex } from "./hash"
import { emptyPlacement, OWN_RUNNER_BONUS } from "./placement"
import type { AgentKind, ModelsByAgent, Placement, Requires } from "./types"

export interface RoutingIntent {
  readonly kind: RoutingRule["kind"]
  readonly itemType?: string | null
}

export interface RoutingResolution {
  readonly rule: RoutingRule | null
  readonly placement: Placement
  readonly byAgent: ModelsByAgent
  readonly required: boolean
  readonly reason: string
}

/** Compile workflow intent into the existing placement and model choices, without performing execution. */
export const resolveRouting = (
  policy: RoutingPolicy | null | undefined,
  intent: RoutingIntent,
): RoutingResolution => {
  const rule = policy?.rules.find(
    (r) => r.kind === intent.kind && (!r.itemTypes?.length || r.itemTypes.includes(intent.itemType ?? "")),
  )
  if (!rule)
    return {
      rule: null,
      placement: emptyPlacement,
      byAgent: {},
      required: false,
      reason: "Project presets and native profile defaults",
    }
  const required = rule.mode === "required"
  return {
    rule,
    placement: routingChoicePlacement(rule),
    byAgent: rule.byAgent,
    required,
    reason: `${required ? "Required" : "Preferred"} route from ${rule.label}`,
  }
}

/**
 * Decision 0154 (review 10-23 #5, "required means required"): does a required validator route say this item is to be
 * validated? The item's own `routing.validator` with mode `required`, or a required project validator rule that names
 * the item's type (a rule for every type only says where validators run, not that every item gets one).
 */
export const validatorRequired = (
  policy: RoutingPolicy | null | undefined,
  itemType: string,
  routing: { readonly validator?: { readonly mode: RoutingChoice["mode"] } } | null | undefined,
): boolean =>
  routing?.validator?.mode === "required" ||
  (policy?.rules.some(
    (r) => r.kind === "validator" && r.mode === "required" && (r.itemTypes?.includes(itemType) ?? false),
  ) ??
    false)

/** Opaque identity shared by native probes and hosted subscriptions; no credential or email leaves the probe. */
export const subscriptionAccountKey = (
  provider: "claude" | "codex",
  email: string | null | undefined,
): string | undefined => {
  const identity = email?.trim().toLowerCase()
  return identity ? sha256Hex(`${provider}:${identity}`) : undefined
}

/** Compile a native route without inventing a workflow rule for a per-task choice. */
export const routingChoicePlacement = (
  choice: Pick<RoutingChoice, "agent" | "runnerId" | "profileId" | "mode"> &
    Partial<Pick<RoutingChoice, "avoid" | "agents">>,
): Placement => {
  // decision 0118: several allowed CLIs compile to `requires.agent` ("any of")
  const agents = routeAgents(choice)
  const match: Requires = {
    ...(agents.length > 0 ? { agent: [agents[0] as AgentKind, ...agents.slice(1)] } : {}),
    ...(choice.runnerId ? { runner: choice.runnerId } : {}),
    ...(choice.profileId ? { profile: choice.profileId } : {}),
  }
  // decision 0095: avoided places are hard in either mode
  const avoid: Requires = choice.avoid && choice.avoid.length > 0 ? { avoid: choice.avoid } : {}
  return {
    ...emptyPlacement,
    requires: choice.mode === "required" ? { ...match, ...avoid } : avoid,
    prefers: choice.mode === "preferred" ? [{ match, weight: OWN_RUNNER_BONUS * 2 }] : [],
  }
}

/**
 * Decision 0118: the one CLI family a turn or a lead summary runs on under a route: a required route keeps `own` when it
 * allows it, else its first CLI; a preferred route or none keeps `own`, else the route's first CLI.
 */
export const routedFamily = (
  route: Pick<RoutingResolution, "rule" | "required">,
  own: AgentKind | null,
): AgentKind | null => {
  const allowed = routeAgents(route.rule)
  if (route.required && allowed.length > 0) return own && allowed.includes(own) ? own : (allowed[0] ?? null)
  return own ?? allowed[0] ?? null
}
